import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ToastTone = 'info' | 'success' | 'warning' | 'error';

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  /** 0-100, undefined = no progress bar */
  progress?: number;
}

interface ToastContextValue {
  showToast: (message: string, tone?: ToastTone, durationMs?: number) => void;
  /** Show a toast with a progress bar. Returns controls to update/dismiss it. */
  showProgressToast: (message: string) => ProgressToastControls;
}

export interface ProgressToastControls {
  /** Update progress (0-100) and optionally the message */
  update: (progress: number, message?: string) => void;
  /** Mark as complete — auto-dismisses after a short delay */
  complete: (message?: string) => void;
  /** Show error state — stays until manually dismissed */
  error: (message: string) => void;
  /** Dismiss immediately */
  dismiss: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_DURATION = 4000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timerId = timersRef.current.get(id);
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      timersRef.current.delete(id);
    }

    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, tone: ToastTone = 'info', durationMs: number = DEFAULT_DURATION) => {
      idRef.current += 1;
      const id = idRef.current;

      setToasts((current) => [...current, { id, message, tone }]);

      const timerId = window.setTimeout(() => dismiss(id), durationMs);
      timersRef.current.set(id, timerId);
    },
    [dismiss]
  );

  const showProgressToast = useCallback(
    (message: string): ProgressToastControls => {
      idRef.current += 1;
      const id = idRef.current;

      setToasts((current) => [...current, { id, message, tone: 'info', progress: 0 }]);

      const update = (progress: number, msg?: string) => {
        setToasts((current) =>
          current.map((t) =>
            t.id === id ? { ...t, progress: Math.min(100, Math.max(0, progress)), ...(msg ? { message: msg } : {}) } : t
          )
        );
      };

      const complete = (msg?: string) => {
        setToasts((current) =>
          current.map((t) =>
            t.id === id ? { ...t, progress: 100, tone: 'success' as ToastTone, ...(msg ? { message: msg } : {}) } : t
          )
        );
        const timerId = window.setTimeout(() => dismiss(id), 2000);
        timersRef.current.set(id, timerId);
      };

      const error = (msg: string) => {
        setToasts((current) =>
          current.map((t) =>
            t.id === id ? { ...t, tone: 'error' as ToastTone, message: msg, progress: undefined } : t
          )
        );
        const timerId = window.setTimeout(() => dismiss(id), 8000);
        timersRef.current.set(id, timerId);
      };

      return {
        update,
        complete,
        error,
        dismiss: () => dismiss(id),
      };
    },
    [dismiss]
  );

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      timersRef.current.clear();
    };
  }, []);

  const value = useMemo(() => ({ showToast, showProgressToast }), [showToast, showProgressToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast-bubble toast-${toast.tone}`} role="status">
            <div className="toast-content">
              <span>{toast.message}</span>
              {toast.progress !== undefined && (
                <div className="toast-progress-track">
                  <div
                    className="toast-progress-bar"
                    style={{ width: `${toast.progress}%` }}
                    role="progressbar"
                    aria-valuenow={toast.progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              )}
            </div>
            <button
              type="button"
              className="toast-dismiss"
              onClick={() => dismiss(toast.id)}
              aria-label="Hinweis schließen"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};
